import React from 'react';

import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { AVAILABILITY, ONLINE_PURCHASE } from '~/consts/schedule';

import { formatDateShort, formatDateSlashesWithClockInSydney } from '~/services/TimeService';

import { scheduleForType } from '~/utils/scheduleForType';

import CampaignScheduleValuePillCell from './CampaignScheduleValuePillCell';
import { CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT } from './campaignScheduleTabLayout';
import type { BrandSchedule, CampaignScheduleParentRow } from './types';

interface DateRangeBoxProps {
  title: string;
  start?: string;
  end?: string;
}

function DateRangeBox({ title, start, end }: DateRangeBoxProps) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, flex: 1 }}>
      <Typography fontWeight={700} gutterBottom variant="subtitle2">
        {title}
      </Typography>
      <Stack alignItems="center" direction="row" spacing={2}>
        <Box>
          <Typography color="text.secondary" variant="caption">
            Start (Australia/Sydney)
          </Typography>
          <Typography variant="body2">{start ? formatDateSlashesWithClockInSydney(start) : '—'}</Typography>
        </Box>
        <Typography color="text.secondary">→</Typography>
        <Box>
          <Typography color="text.secondary" variant="caption">
            End (Australia/Sydney)
          </Typography>
          <Typography variant="body2">{end ? formatDateSlashesWithClockInSydney(end) : '—'}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

interface Props {
  brandSchedules: Array<BrandSchedule>;
  parentRows: Array<CampaignScheduleParentRow>;
}

export default function ImportedSalesforceDatesSection({ brandSchedules, parentRows }: Props) {
  const bookByStart = scheduleForType(brandSchedules, AVAILABILITY, 'start');
  const bookByEnd = scheduleForType(brandSchedules, AVAILABILITY, 'end');
  const offlineStart = scheduleForType(brandSchedules, ONLINE_PURCHASE, 'start');
  const offlineEnd = scheduleForType(brandSchedules, ONLINE_PURCHASE, 'end');

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
      <Typography fontWeight={700} sx={{ mb: 2 }} variant="subtitle1">
        Imported Salesforce Dates
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <DateRangeBox end={bookByEnd} start={bookByStart} title="Book By Dates" />
        <DateRangeBox end={offlineEnd} start={offlineStart} title="Available Offline" />
      </Stack>

      <TableContainer sx={{ maxWidth: '100%', mt: 3, overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>Salesforce Campaign Schedules</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell>Type</TableCell>
              <TableCell>Start Offline</TableCell>
              <TableCell>End Offline</TableCell>
              <TableCell>Start Online</TableCell>
              <TableCell>End Online</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Region</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parentRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce campaign schedules found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {parentRows.map((parentRow) => (
              <TableRow key={parentRow.id}>
                <TableCell>
                  <CampaignScheduleValuePillCell label="types" values={parentRow.campaignTypes} />
                </TableCell>
                <TableCell>{parentRow.startOffline ? formatDateShort(parentRow.startOffline) : '—'}</TableCell>
                <TableCell>{parentRow.endOffline ? formatDateShort(parentRow.endOffline) : '—'}</TableCell>
                <TableCell>{parentRow.startOnline ? formatDateShort(parentRow.startOnline) : '—'}</TableCell>
                <TableCell>{parentRow.endOnline ? formatDateShort(parentRow.endOnline) : '—'}</TableCell>
                <TableCell>
                  <CampaignScheduleValuePillCell label="brands" values={parentRow.brands} />
                </TableCell>
                <TableCell>
                  <CampaignScheduleValuePillCell label="regions" values={parentRow.markets} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
